import * as React from 'react';

import type { BoardFileType } from '@/features/CustomFeedV2/common';
import { useFetchFeed } from '@/features/CustomFeedV2/hooks';

const INITIAL_INTERVAL = 5000; // 5 seconds
const MAX_TOTAL_TIME = 120000; // Stop polling after 120 seconds

export const usePollDocumentsFeed = (
  feedId: string,
  callback?: CallbackType
) => {
  const [pollingEnabled, setPollingEnabled] = React.useState(false);
  const [pollInterval, setPollInterval] = React.useState(INITIAL_INTERVAL);
  const totalPollingTime = React.useRef(0);

  const refetchInterval = React.useMemo(() => {
    if (!pollingEnabled) return false;
    return pollInterval;
  }, [pollingEnabled, pollInterval]);

  const enabled = React.useMemo(() => {
    if (!feedId) return false;
    return pollingEnabled;
  }, [feedId, pollingEnabled]);

  const {
    data: feedData,
    isFetching,
    refetch,
  } = useFetchFeed(feedId, refetchInterval, enabled);

  React.useEffect(() => {
    if (!pollingEnabled || !feedData?.documents) {
      return;
    }

    const allProcessed = feedData.documents.every(
      (doc) => doc.status === 'COMPLETED' || doc.status === 'FAILED'
    );

    if (allProcessed || totalPollingTime.current >= MAX_TOTAL_TIME) {
      stopPolling();
    } else {
      setPollInterval((prev) => prev * 2);
      totalPollingTime.current += pollInterval;
    }
  }, [feedData, pollInterval, pollingEnabled]);

  React.useEffect(() => {
    if (feedData && feedData.documents) {
      callback?.(feedData.documents);
    }
  }, [feedData, callback]);

  const startPolling = () => {
    if (pollingEnabled) return;

    totalPollingTime.current = 0;
    setPollInterval(INITIAL_INTERVAL);
    setPollingEnabled(true);
    refetch();
  };

  const stopPolling = () => {
    setPollingEnabled(false);
    setPollInterval(INITIAL_INTERVAL);
  };

  return { feedData, isFetching, startPolling, stopPolling, pollingEnabled };
};

type CallbackType = (documents: BoardFileType[]) => void;
